import { useEffect, useState } from "react";
import Link from "next/link";

export type StudyAvailability = { independent_ai: boolean; academic_uploads: boolean; assessment_ai: boolean; voice: boolean; legacy_ai: boolean };
export function useStudyAvailability() {
  const [status, setStatus] = useState<StudyAvailability | null>(null);
  useEffect(() => {
    let active = true;
    void fetch("/api/study/status", { cache: "no-store" }).then(async response => {
      const result = response.ok ? await response.json() : null;
      if (active) setStatus(result);
    }).catch(() => { if (active) setStatus(null); });
    return () => { active = false; };
  }, []);
  return status;
}

export function StudyAvailabilityDetails({ status, purchasesReady }: { status: StudyAvailability | null; purchasesReady: boolean | null }) {
  return <div role="status" className="my-4 rounded-xl border border-slate-200 p-4 text-sm leading-6 dark:border-slate-700">
    <p className="font-semibold">Current service availability</p>
    {status ? <ul>{([
      ["independent_ai", "Independent coaching"], ["academic_uploads", "Academic uploads"],
      ["assessment_ai", "Assessment support"], ["voice", "Voice"], ["legacy_ai", "Other study AI tools"],
    ] as const).map(([key, label]) => <li key={key}>{label}: {status[key] === true ? "available for eligible accounts and permitted use" : "not currently available"}.</li>)}</ul> : <p>Live feature availability is loading. Your workspace remains the authority for feature-specific availability.</p>}
    <p>New purchases: {purchasesReady === null ? <>see <Link href="/pricing" className="underline">Pricing</Link> for current plans and checkout availability</> : purchasesReady ? "available, subject to the terms shown at checkout" : <>not currently available. See <Link href="/pricing" className="underline">Pricing</Link> for the current options</>}.</p>
    <p>Saved work, downloads, privacy requests and cancellation controls remain separate. Service availability does not grant rights to material or override assessment rules. <Link href="/legal/service-transparency" className="underline">Processing and availability details</Link>.</p>
  </div>;
}

export default function StudyAvailabilityNotice() {
  const status = useStudyAvailability();
  // Unknown is distinct from a confirmed closure, including during server rendering.
  const [purchasesReady, setPurchasesReady] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    void fetch("/api/shared-platform/status", { cache: "no-store" }).then(async response => {
      const release = response.ok ? await response.json() : null;
      if (active) setPurchasesReady(typeof release?.purchases_open === "boolean" ? release.purchases_open : null);
    }).catch(() => { if (active) setPurchasesReady(null); });
    return () => { active = false; };
  }, []);
  return <StudyAvailabilityDetails status={status} purchasesReady={purchasesReady} />;
}
